import { toKauppaSlug } from '../../components/DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import { PriceType } from '../../components/HorizontalSubscriptionCard/types.js';
import { SortType } from '../../components/ProductGridCoCo/ProductGridSortCoCo.js';
import {
  DeviceSelectionCategoryCoCo,
  DeviceSelectionProductTypeCoCo,
  DeviceVariantProductTypeCoCo,
  OfferSubCategory,
} from '../enums.js';
import { isCoreDeviceCategory } from './deviceSelectionCategoryPredicatesCoCo.js';
import { getDeviceSelectionCategories } from './deviceSelectionCategoryUtilsCoCo.js';
import { DEVICE_SPECIFICATION_NAMES } from './deviceSpecificationUtilsCoCo.js';
import { centsToEuros, eurosToCents } from './priceUtils.js';
import {
  createBrandAndPriceFilters,
  gridFilter,
  keepSelectedFilterOptions,
  matchesSearch,
} from './productListUtilsCoCo.js';
import { getLocalizedString, getSpecValueFromSpecs } from './productOfferingsUtils.js';
import type { DeviceSelectionCategoryCountCoCo } from './deviceSelectionCategoryUtilsCoCo.js';
import type { Filters } from '../../components/ProductGridCoCo/ProductGridFilterCoCo.js';
import type { DeviceVariantGroup } from '../../generated/api/deviceVariantGroup.js';
import type { ProductSpecification } from '../../generated/api/productSpecification.js';

export interface DeviceSelectionDeviceCoCo {
  id: string;
  name: string;
  brand: string;
  category: DeviceSelectionCategoryCoCo;
  // Monthly price in cents
  monthlyPrice: number;
  // Set for purchased devices, including the EPP one-time payment category.
  oneTimePrice?: number;
  imageUrl?: string;
  // Device details page path
  detailsPath?: string;
}

export type DeviceVariantGroupsByTypeCoCo = Record<DeviceVariantProductTypeCoCo, DeviceVariantGroup[]>;

export type DeviceSelectionDevicesByProductTypeCoCo = Record<
  DeviceSelectionProductTypeCoCo,
  DeviceSelectionDeviceCoCo[]
>;

const DEVICE_TYPE_CATEGORIES: Record<string, DeviceSelectionCategoryCoCo> = {
  phone: DeviceSelectionCategoryCoCo.PHONES,
  smartphone: DeviceSelectionCategoryCoCo.PHONES,
  foldablesmartphone: DeviceSelectionCategoryCoCo.PHONES,
  mobilephone: DeviceSelectionCategoryCoCo.PHONES,
  tablet: DeviceSelectionCategoryCoCo.TABLETS,
  computer: DeviceSelectionCategoryCoCo.COMPUTERS,
  laptop: DeviceSelectionCategoryCoCo.COMPUTERS,
  desktop: DeviceSelectionCategoryCoCo.COMPUTERS,
  notebook: DeviceSelectionCategoryCoCo.COMPUTERS,
  accessory: DeviceSelectionCategoryCoCo.ACCESSORIES,
  accessories: DeviceSelectionCategoryCoCo.ACCESSORIES,
};

const getExplicitDeviceSelectionCategory = (specifications: ProductSpecification[]) => {
  const categoryValue = getSpecValueFromSpecs(specifications, DEVICE_SPECIFICATION_NAMES.DEVICE_SELECTION_CATEGORY);
  return Object.values(DeviceSelectionCategoryCoCo).find(category => category === categoryValue);
};

const getDeviceTypeCategory = (deviceType?: string) =>
  deviceType ? DEVICE_TYPE_CATEGORIES[deviceType.toLowerCase().replace(/\s+/g, '')] : undefined;

// Variant group offerings are typed e.g. as Device / Smartphone, so the subcategory is also used as the device type
export const getDeviceVariantGroupCategory = (group: DeviceVariantGroup): DeviceSelectionCategoryCoCo => {
  const specifications = group.variantSpecification ?? [];
  const explicitCategory = getExplicitDeviceSelectionCategory(specifications);
  if (explicitCategory) {
    return explicitCategory;
  }
  const subcategory = group.productOfferings[0]?.productOfferingType?.subcategory;
  if (subcategory === OfferSubCategory.QUANTITY_BASED_ACCESSORY) {
    return DeviceSelectionCategoryCoCo.ACCESSORIES;
  }
  return (
    getDeviceTypeCategory(getSpecValueFromSpecs(specifications, DEVICE_SPECIFICATION_NAMES.DEVICE_TYPE)) ||
    getDeviceTypeCategory(subcategory) ||
    DeviceSelectionCategoryCoCo.OTHER_DEVICES
  );
};

export const getDeviceSelectionPrice = (device: DeviceSelectionDeviceCoCo) =>
  device.oneTimePrice ?? device.monthlyPrice;

// Same case-insensitive brand lookup as for device offers in the device catalogue
const getVariantGroupBrand = (group: DeviceVariantGroup): string | undefined =>
  group.variantSpecification?.find(spec => spec.name?.toLowerCase() === DEVICE_SPECIFICATION_NAMES.BRAND)?.value?.[0]
    ?.value;

const hasSupportedPriceType = (group: DeviceVariantGroup) =>
  group.variantPrice.priceType === PriceType.RECURRING_CHARGE ||
  group.variantPrice.priceType === PriceType.ONE_TIME_CHARGE;

const toAbsolutePath = (path: string) => (path.startsWith('/') || /^https?:\/\//.test(path) ? path : `/${path}`);

// Uses the variant group path when available, otherwise the slug of the first offering, as in the device catalogue
export const getDeviceVariantGroupDetailsPath = (group: DeviceVariantGroup): string | undefined => {
  if (group.path?.trim()) {
    return toAbsolutePath(group.path.trim());
  }
  const firstOffering = group.productOfferings[0];
  const slug = firstOffering ? toKauppaSlug(getLocalizedString(firstOffering.name)) : '';
  return slug ? `/${slug}` : undefined;
};

// The brand falls back to the first word of the name, e.g. "Apple" of "Apple iPhone 17 Pro 256 GB"
export const toDeviceSelectionVariantDevice = (group: DeviceVariantGroup): DeviceSelectionDeviceCoCo => {
  const name = getLocalizedString(group.variantGroupName);
  const price = eurosToCents(group.variantPrice.priceWithoutTax);
  const isMonthly = group.variantPrice.priceType === PriceType.RECURRING_CHARGE;
  const device: DeviceSelectionDeviceCoCo = {
    id: group.variantGroupId,
    name,
    brand: getVariantGroupBrand(group) || name.split(' ')[0],
    category: getDeviceVariantGroupCategory(group),
    monthlyPrice: isMonthly ? price : 0,
  };
  if (group.variantImageUrl) {
    device.imageUrl = group.variantImageUrl;
  }
  const detailsPath = getDeviceVariantGroupDetailsPath(group);
  if (detailsPath) {
    device.detailsPath = detailsPath;
  }
  if (!isMonthly) {
    device.oneTimePrice = price;
  }
  return device;
};

// Devices are selected by variant group, so there can be only one device per variant group
const mergeDevicesByVariantGroup = (devices: DeviceSelectionDeviceCoCo[]): DeviceSelectionDeviceCoCo[] => {
  const devicesByVariantGroup = new Map<string, DeviceSelectionDeviceCoCo>();
  for (const device of devices) {
    const existing = devicesByVariantGroup.get(device.id);
    // Prefer one-time payment for shared non-core variants; otherwise keep the first variant group.
    const preferOneTimePayment =
      device.category === DeviceSelectionCategoryCoCo.ACCESSORIES_AND_OTHER_DEVICES &&
      existing?.category !== DeviceSelectionCategoryCoCo.ACCESSORIES_AND_OTHER_DEVICES;
    if (!existing || preferOneTimePayment) {
      devicesByVariantGroup.set(device.id, device);
    }
  }
  return [...devicesByVariantGroup.values()];
};

// EPP selections contain the monthly EPP variant groups and the non-core purchased ones as one-time payment devices
export const toDeviceSelectionVariantDevices = (
  variantGroups: DeviceVariantGroupsByTypeCoCo,
  productType: DeviceSelectionProductTypeCoCo = DeviceSelectionProductTypeCoCo.EPP
): DeviceSelectionDeviceCoCo[] => {
  // Variant groups with an unknown price type are skipped, as their price can't be shown as monthly or one-time
  const toDevices = (groups: DeviceVariantGroup[]) =>
    groups.filter(hasSupportedPriceType).map(toDeviceSelectionVariantDevice);
  const purchasedDevices = toDevices(variantGroups[DeviceVariantProductTypeCoCo.Device]);
  if (productType === DeviceSelectionProductTypeCoCo.PURCHASED) {
    return mergeDevicesByVariantGroup(
      purchasedDevices.map(device =>
        device.category === DeviceSelectionCategoryCoCo.ACCESSORIES_AND_OTHER_DEVICES
          ? { ...device, category: DeviceSelectionCategoryCoCo.ACCESSORIES }
          : device
      )
    );
  }
  return mergeDevicesByVariantGroup([
    ...toDevices(variantGroups[DeviceVariantProductTypeCoCo.EPP]),
    ...purchasedDevices
      .filter(({ category }) => !isCoreDeviceCategory(category))
      .map(device => ({ ...device, category: DeviceSelectionCategoryCoCo.ACCESSORIES_AND_OTHER_DEVICES })),
  ]);
};

export const getDeviceSelectionDevices = (
  category: DeviceSelectionCategoryCoCo | undefined,
  devices: DeviceSelectionDeviceCoCo[]
) => (category ? devices.filter(device => device.category === category) : []);

// Selected devices whose category isn't available for the product type are dropped
export const filterDeviceIdsByProductType = (
  deviceIds: string[],
  productType: DeviceSelectionProductTypeCoCo | undefined,
  devices: DeviceSelectionDeviceCoCo[]
) => {
  const availableCategories = new Set(getDeviceSelectionCategories(productType).map(({ category }) => category));
  const availableDeviceIds = new Set(
    devices.filter(({ category }) => availableCategories.has(category)).map(({ id }) => id)
  );
  return deviceIds.filter(id => availableDeviceIds.has(id));
};

// Counts are returned in the category display order of the product type
export const getSelectedDeviceCategoryCounts = (
  deviceIds: string[],
  productType: DeviceSelectionProductTypeCoCo | undefined,
  devices: DeviceSelectionDeviceCoCo[]
): DeviceSelectionCategoryCountCoCo[] => {
  const selectedIds = new Set(deviceIds);
  const countsByCategory = new Map<DeviceSelectionCategoryCoCo, number>();
  devices
    .filter(({ id }) => selectedIds.has(id))
    .forEach(({ category }) => countsByCategory.set(category, (countsByCategory.get(category) ?? 0) + 1));
  return getDeviceSelectionCategories(productType).map(({ category }) => ({
    category,
    count: countsByCategory.get(category) ?? 0,
  }));
};

export const toggleDeviceId = (selectedIds: string[], deviceId: string) =>
  selectedIds.includes(deviceId) ? selectedIds.filter(id => id !== deviceId) : [...selectedIds, deviceId];

// Only the given devices are (de)selected, selections of other devices are kept
export const toggleAllDeviceIds = (selectedIds: string[], deviceIds: string[], allSelected: boolean) => {
  const toggledIds = new Set(deviceIds);
  const otherSelectedIds = selectedIds.filter(id => !toggledIds.has(id));
  return allSelected ? otherSelectedIds : [...otherSelectedIds, ...deviceIds];
};

export type DeviceSelectionFiltersCoCo = Filters<DeviceSelectionDeviceCoCo>;

export const createDeviceSelectionFilters = (
  devices: DeviceSelectionDeviceCoCo[],
  previous?: DeviceSelectionFiltersCoCo
): DeviceSelectionFiltersCoCo => {
  const filters = createBrandAndPriceFilters(devices, {
    getBrand: device => device.brand,
    getPriceEuros: device => centsToEuros(getDeviceSelectionPrice(device)),
    priceRangeSize: 10,
    priceRoundingStep: 10,
  });
  return previous ? keepSelectedFilterOptions(previous, filters) : filters;
};

type DeviceCompareFn = (left: DeviceSelectionDeviceCoCo, right: DeviceSelectionDeviceCoCo) => number;
const DEVICE_COMPARE_FNS: Record<SortType, DeviceCompareFn> = {
  // Creation dates are not available yet; preserve the source order for newest first.
  [SortType.ORDER_NEWEST]: () => 0,
  [SortType.ORDER_ALPHABETICAL]: (left, right) =>
    `${left.brand}${left.name}`.localeCompare(`${right.brand}${right.name}`),
  [SortType.ORDER_CHEAPEST]: (left, right) => getDeviceSelectionPrice(left) - getDeviceSelectionPrice(right),
  [SortType.ORDER_COSTLIEST]: (left, right) => getDeviceSelectionPrice(right) - getDeviceSelectionPrice(left),
};

export interface DeviceSelectionListOptionsCoCo {
  search: string;
  filters: DeviceSelectionFiltersCoCo;
  sort: SortType;
  selectedFirstIds: ReadonlySet<string>;
}

export const getVisibleDevices = (
  devices: DeviceSelectionDeviceCoCo[],
  { search, filters, sort, selectedFirstIds }: DeviceSelectionListOptionsCoCo
) => {
  const visibleDevices = devices
    .filter(({ name, brand }) => matchesSearch(search, [name, brand]))
    .filter(gridFilter(filters))
    .sort(DEVICE_COMPARE_FNS[sort]);
  return selectedFirstIds.size === 0
    ? visibleDevices
    : [
        ...visibleDevices.filter(({ id }) => selectedFirstIds.has(id)),
        ...visibleDevices.filter(({ id }) => !selectedFirstIds.has(id)),
      ];
};
